import React, { useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import SocialSignIn from '../components/SocialSignIn';
import SocialPendingStep from '../components/SocialPendingStep';
import { safeReturnPath } from '../lib/returnPath';
import './AuthPages.css';

const LoginPage = () => {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // WT-5 QA: same-site paths only (react-router falls back to a full page load
  // for a "//host" path), like Register.
  const from = safeReturnPath(location.state?.from);
  const expired = !!location.state?.expired; // W-10: sent here by SessionExpiryRedirect
  // WT-28: signed-out "Create club" (CreateClubPage sends state.from
  // '/create-club') used to land on a generic "Welcome". Say why they're
  // here, and offer a new account just as clearly. Every other path keeps
  // the generic wording.
  const creatingClub = from === '/create-club';

  const [form, setForm] = useState({ email: location.state?.email || '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submittedRef = useRef(false); // our own sign-in decides where to go next
  // B-53: a first-time Apple/Google sign-in waiting for the DOB + terms step.
  const [pendingSocial, setPendingSocial] = useState(null);

  // Where a fresh sign-in lands — password and Apple/Google alike.
  const goAfterSignIn = (userData) => {
    const memberships = userData?.memberships || [];
    // B-65: came from a confirm link ("Sign in to confirm your email") → back
    // there first, so it confirms now (ahead of the B-59 code redirect).
    if (from && from.startsWith('/confirm-email/')) { navigate(from, { replace: true }); return; }
    // B-59: a club invite is waiting for the email code → My Clubs, where
    // the code box is (ahead of the return path and the sponsor shortcut).
    if (userData?.emailVerified === false && userData?.invitesWaiting > 0) {
      navigate('/my-clubs', { replace: true }); return;
    }
    // If redirected here from a protected route, go back there
    if (from) { navigate(from, { replace: true }); return; }
    // Sponsor with one club → go straight to scan
    const sponsorMembership = memberships.find(m => m.role === 'sponsor');
    if (sponsorMembership && memberships.length === 1) {
      navigate(`/${sponsorMembership.clubSlug}/scan`, { replace: true });
    } else {
      // Multi-club users (or single-club) → /my-clubs handles redirect
      navigate('/my-clubs', { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    submittedRef.current = true;
    setError(''); setLoading(true);
    try {
      goAfterSignIn(await login(form.email, form.password));
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Already signed in and arriving cold (e.g. marakin.co.uk's "Account login"):
  // skip the form. Arrivals carrying state (invite "sign in as…", session
  // expiry, a return path) still get the form so they can switch account.
  if (!authLoading && user && !location.state && !submittedRef.current) return <Navigate to="/my-clubs" replace />;

  if (pendingSocial) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-wrap">
            <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
          </div>
          <SocialPendingStep pending={pendingSocial} onDone={goAfterSignIn} onCancel={() => setPendingSocial(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        <h1>{creatingClub ? 'Set up your club' : 'Welcome'}</h1>
        <p className="auth-subtitle">
          {from && from.startsWith('/confirm-email/') ? 'Sign in to confirm your email'
            : creatingClub ? 'Sign in to set up your club, or create an account if you’re new to Marakin.'
            : 'Sign in to your Marakin account'}
        </p>

        {expired && !error && (
          <div className="auth-error" style={{ background: 'rgba(196, 148, 58, 0.14)', color: '#8a5f10' }}>
            <AlertCircle size={16} /> Your session expired while you were away — please sign in again and we'll take you straight back.
          </div>
        )}
        {error && (
          <div className="auth-error">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <SocialSignIn
          onBeforeSignIn={() => { submittedRef.current = true; }}
          onSignedIn={goAfterSignIn}
          onNeedsProfile={setPendingSocial}
        />

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="label">Email</label>
            <input className="input" type="email" placeholder="you@example.com"
              value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
          </div>
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label className="label">Password</label>
              <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                Forgot password?
              </Link>
            </div>
            <PasswordInput placeholder="••••••••"
              value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required />
          </div>
          <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : <><LogIn size={16} /> Sign in</>}
          </button>
        </form>

        <p className="auth-switch">
          {/* WT-5: a new account keeps the return path (e.g. the club page it
              came from); a confirm link's own path is for this sign-in only. */}
          {creatingClub ? 'New to Marakin? ' : "Don't have an account? "}
          <Link to="/register" state={from && !from.startsWith('/confirm-email/') ? { from } : undefined}>
            {creatingClub ? 'Create an account' : 'Register here'}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
